import React, { useState } from 'react';
import { InputNumber, Input, Select, Collapse, Space } from '@douyinfe/semi-ui';
import { ColorPicker } from '../../components/ColorPicker';
import styles from './index.module.scss';

const { Option } = Select;
const { Panel } = Collapse;

const StyleEditor = ({ value = {}, onChange, elementType = 'text' }) => {
    const [activeKey, setActiveKey] = useState(
        elementType === 'module' ? ['layout', 'border'] : ['layout', 'border', 'background']
    );

    const updateStyle = (key, val) => {
        const newValue = { ...value, [key]: val };
        onChange(newValue);
    };

    const updateNestedStyle = (category, key, val) => {
        const newValue = { ...value };
        if (!newValue[category]) {
            newValue[category] = {};
        }
        newValue[category][key] = val;
        onChange(newValue);
    };

    const updateGradientBackground = (customValue = null) => {
        const currentValue = customValue || value;
        const gradientType = currentValue.gradientType || 'linear';
        const gradientStart = currentValue.gradientStart || '#ffffff';
        const gradientEnd = currentValue.gradientEnd || '#000000';
        const gradientAngle = currentValue.gradientAngle || 0;

        let backgroundImage;
        if (gradientType === 'linear') {
            backgroundImage = `linear-gradient(${gradientAngle}deg, ${gradientStart}, ${gradientEnd})`;
        } else {
            backgroundImage = `radial-gradient(circle, ${gradientStart}, ${gradientEnd})`;
        }

        const newValue = { ...currentValue };
        newValue.backgroundImage = backgroundImage;
        newValue.backgroundColor = '';
        onChange(newValue);
    };

    // 布局和定位样式
    const renderLayoutStyles = () => (
        <Panel header="布局定位" itemKey="layout">
            <div className={styles.styleSectionContent}>
                <Space vertical style={{ width: '100%' }}>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>边距:</span>
                        <Space>
                            <InputNumber
                                placeholder="上"
                                value={parseInt(value.marginTop) || 0}
                                onChange={val => updateStyle('marginTop', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="右"
                                value={parseInt(value.marginRight) || 0}
                                onChange={val => updateStyle('marginRight', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="下"
                                value={parseInt(value.marginBottom) || 0}
                                onChange={val => updateStyle('marginBottom', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="左"
                                value={parseInt(value.marginLeft) || 0}
                                onChange={val => updateStyle('marginLeft', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                        </Space>
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>内边距:</span>
                        <Space>
                            <InputNumber
                                placeholder="上"
                                value={parseInt(value.paddingTop) || 0}
                                onChange={val => updateStyle('paddingTop', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="右"
                                value={parseInt(value.paddingRight) || 0}
                                onChange={val => updateStyle('paddingRight', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="下"
                                value={parseInt(value.paddingBottom) || 0}
                                onChange={val => updateStyle('paddingBottom', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                            <InputNumber
                                placeholder="左"
                                value={parseInt(value.paddingLeft) || 0}
                                onChange={val => updateStyle('paddingLeft', `${val}px`)}
                                size="small"
                                style={{ width: 60 }}
                            />
                        </Space>
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>透明度:</span>
                        <InputNumber
                            value={parseFloat(value.opacity) || 1}
                            onChange={val => updateStyle('opacity', val)}
                            min={0}
                            max={1}
                            step={0.1}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>z-index:</span>
                        <InputNumber
                            value={parseInt(value.zIndex) || 1}
                            onChange={val => updateStyle('zIndex', val)}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>裁剪形状:</span>
                        <Select
                            value={value.clipPathType || 'none'}
                            onChange={val => {
                                const newValue = { ...value, clipPathType: val };

                                if (val === 'none') {
                                    newValue.clipPath = '';
                                } else if (val === 'circle') {
                                    const radius = parseInt(value.clipCircleRadius) || 50;
                                    const x = parseInt(value.clipCircleX) || 50;
                                    const y = parseInt(value.clipCircleY) || 50;
                                    newValue.clipPath = `circle(${radius}% at ${x}% ${y}%)`;
                                } else if (val === 'ellipse') {
                                    const radiusX = parseInt(value.clipEllipseRadiusX) || 50;
                                    const radiusY = parseInt(value.clipEllipseRadiusY) || 50;
                                    const x = parseInt(value.clipEllipseX) || 50;
                                    const y = parseInt(value.clipEllipseY) || 50;
                                    newValue.clipPath = `ellipse(${radiusX}% ${radiusY}% at ${x}% ${y}%)`;
                                } else if (val === 'polygon') {
                                    const points = value.clipPolygonPoints || '50% 0%, 0% 100%, 100% 100%';
                                    newValue.clipPath = `polygon(${points})`;
                                } else if (val === 'inset') {
                                    const top = parseInt(value.clipInsetTop) || 0;
                                    const right = parseInt(value.clipInsetRight) || 0;
                                    const bottom = parseInt(value.clipInsetBottom) || 0;
                                    const left = parseInt(value.clipInsetLeft) || 0;
                                    const round = parseInt(value.clipInsetRound) || 0;
                                    newValue.clipPath = round > 0
                                        ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                        : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                } else if (val === 'custom') {
                                    newValue.clipPath = value.clipCustomPath || '';
                                }

                                onChange(newValue);
                            }}
                            style={{ width: '100%' }}
                        >
                            <Option value="none">无裁剪</Option>
                            <Option value="circle">圆形</Option>
                            <Option value="ellipse">椭圆形</Option>
                            <Option value="polygon">多边形</Option>
                            <Option value="inset">矩形内嵌</Option>
                            <Option value="custom">自定义</Option>
                        </Select>
                    </div>

                    {/* 圆形裁剪设置 */}
                    {value.clipPathType === 'circle' && (
                        <>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>半径 (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipCircleRadius) || 50}
                                    onChange={val => {
                                        const radius = val || 50;
                                        const x = parseInt(value.clipCircleX) || 50;
                                        const y = parseInt(value.clipCircleY) || 50;
                                        const clipPath = `circle(${radius}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipCircleRadius: radius, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>中心X (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipCircleX) || 50}
                                    onChange={val => {
                                        const radius = parseInt(value.clipCircleRadius) || 50;
                                        const x = val || 50;
                                        const y = parseInt(value.clipCircleY) || 50;
                                        const clipPath = `circle(${radius}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipCircleX: x, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>中心Y (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipCircleY) || 50}
                                    onChange={val => {
                                        const radius = parseInt(value.clipCircleRadius) || 50;
                                        const x = parseInt(value.clipCircleX) || 50;
                                        const y = val || 50;
                                        const clipPath = `circle(${radius}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipCircleY: y, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* 椭圆形裁剪设置 */}
                    {value.clipPathType === 'ellipse' && (
                        <>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>水平半径 (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipEllipseRadiusX) || 50}
                                    onChange={val => {
                                        const radiusX = val || 50;
                                        const radiusY = parseInt(value.clipEllipseRadiusY) || 50;
                                        const x = parseInt(value.clipEllipseX) || 50;
                                        const y = parseInt(value.clipEllipseY) || 50;
                                        const clipPath = `ellipse(${radiusX}% ${radiusY}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipEllipseRadiusX: radiusX, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>垂直半径 (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipEllipseRadiusY) || 50}
                                    onChange={val => {
                                        const radiusX = parseInt(value.clipEllipseRadiusX) || 50;
                                        const radiusY = val || 50;
                                        const x = parseInt(value.clipEllipseX) || 50;
                                        const y = parseInt(value.clipEllipseY) || 50;
                                        const clipPath = `ellipse(${radiusX}% ${radiusY}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipEllipseRadiusY: radiusY, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>中心X (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipEllipseX) || 50}
                                    onChange={val => {
                                        const radiusX = parseInt(value.clipEllipseRadiusX) || 50;
                                        const radiusY = parseInt(value.clipEllipseRadiusY) || 50;
                                        const x = val || 50;
                                        const y = parseInt(value.clipEllipseY) || 50;
                                        const clipPath = `ellipse(${radiusX}% ${radiusY}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipEllipseX: x, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>中心Y (%):</span>
                                <InputNumber
                                    value={parseInt(value.clipEllipseY) || 50}
                                    onChange={val => {
                                        const radiusX = parseInt(value.clipEllipseRadiusX) || 50;
                                        const radiusY = parseInt(value.clipEllipseRadiusY) || 50;
                                        const x = parseInt(value.clipEllipseX) || 50;
                                        const y = val || 50;
                                        const clipPath = `ellipse(${radiusX}% ${radiusY}% at ${x}% ${y}%)`;
                                        const newValue = { ...value, clipEllipseY: y, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* 多边形裁剪设置 */}
                    {value.clipPathType === 'polygon' && (
                        <>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>预设形状:</span>
                                <Select
                                    value=""
                                    onChange={val => {
                                        const newValue = { ...value, clipPolygonPoints: val };
                                        newValue.clipPath = `polygon(${val})`;
                                        onChange(newValue);
                                    }}
                                    placeholder="选择预设形状"
                                    style={{ width: '100%' }}
                                >
                                    <Option value="50% 0%, 0% 100%, 100% 100%">三角形</Option>
                                    <Option value="20% 0%, 80% 0%, 100% 100%, 0% 100%">梯形</Option>
                                    <Option value="50% 0%, 100% 50%, 50% 100%, 0% 50%">菱形</Option>
                                    <Option value="25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%">六边形</Option>
                                    <Option value="50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%">星形</Option>
                                    <Option value="15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%">八角形</Option>
                                </Select>
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>多边形点坐标:</span>
                                <Input
                                    value={value.clipPolygonPoints || '50% 0%, 0% 100%, 100% 100%'}
                                    onChange={val => {
                                        const newValue = { ...value, clipPolygonPoints: val };
                                        newValue.clipPath = `polygon(${val})`;
                                        onChange(newValue);
                                    }}
                                    placeholder="例: 50% 0%, 0% 100%, 100% 100%"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* 矩形内嵌裁剪设置 */}
                    {value.clipPathType === 'inset' && (
                        <>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>内嵌距离:</span>
                                <Space>
                                    <InputNumber
                                        placeholder="上"
                                        value={parseInt(value.clipInsetTop) || 0}
                                        onChange={val => {
                                            const top = val || 0;
                                            const right = parseInt(value.clipInsetRight) || 0;
                                            const bottom = parseInt(value.clipInsetBottom) || 0;
                                            const left = parseInt(value.clipInsetLeft) || 0;
                                            const round = parseInt(value.clipInsetRound) || 0;
                                            const clipPath = round > 0
                                                ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                                : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                            const newValue = { ...value, clipInsetTop: top, clipPath };
                                            onChange(newValue);
                                        }}
                                        size="small"
                                        style={{ width: 60 }}
                                    />
                                    <InputNumber
                                        placeholder="右"
                                        value={parseInt(value.clipInsetRight) || 0}
                                        onChange={val => {
                                            const top = parseInt(value.clipInsetTop) || 0;
                                            const right = val || 0;
                                            const bottom = parseInt(value.clipInsetBottom) || 0;
                                            const left = parseInt(value.clipInsetLeft) || 0;
                                            const round = parseInt(value.clipInsetRound) || 0;
                                            const clipPath = round > 0
                                                ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                                : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                            const newValue = { ...value, clipInsetRight: right, clipPath };
                                            onChange(newValue);
                                        }}
                                        size="small"
                                        style={{ width: 60 }}
                                    />
                                    <InputNumber
                                        placeholder="下"
                                        value={parseInt(value.clipInsetBottom) || 0}
                                        onChange={val => {
                                            const top = parseInt(value.clipInsetTop) || 0;
                                            const right = parseInt(value.clipInsetRight) || 0;
                                            const bottom = val || 0;
                                            const left = parseInt(value.clipInsetLeft) || 0;
                                            const round = parseInt(value.clipInsetRound) || 0;
                                            const clipPath = round > 0
                                                ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                                : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                            const newValue = { ...value, clipInsetBottom: bottom, clipPath };
                                            onChange(newValue);
                                        }}
                                        size="small"
                                        style={{ width: 60 }}
                                    />
                                    <InputNumber
                                        placeholder="左"
                                        value={parseInt(value.clipInsetLeft) || 0}
                                        onChange={val => {
                                            const top = parseInt(value.clipInsetTop) || 0;
                                            const right = parseInt(value.clipInsetRight) || 0;
                                            const bottom = parseInt(value.clipInsetBottom) || 0;
                                            const left = val || 0;
                                            const round = parseInt(value.clipInsetRound) || 0;
                                            const clipPath = round > 0
                                                ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                                : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                            const newValue = { ...value, clipInsetLeft: left, clipPath };
                                            onChange(newValue);
                                        }}
                                        size="small"
                                        style={{ width: 60 }}
                                    />
                                </Space>
                            </div>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>圆角半径:</span>
                                <InputNumber
                                    value={parseInt(value.clipInsetRound) || 0}
                                    onChange={val => {
                                        const top = parseInt(value.clipInsetTop) || 0;
                                        const right = parseInt(value.clipInsetRight) || 0;
                                        const bottom = parseInt(value.clipInsetBottom) || 0;
                                        const left = parseInt(value.clipInsetLeft) || 0;
                                        const round = val || 0;
                                        const clipPath = round > 0
                                            ? `inset(${top}px ${right}px ${bottom}px ${left}px round ${round}px)`
                                            : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
                                        const newValue = { ...value, clipInsetRound: round, clipPath };
                                        onChange(newValue);
                                    }}
                                    min={0}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* 自定义裁剪设置 */}
                    {value.clipPathType === 'custom' && (
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>自定义clip-path:</span>
                            <Input
                                value={value.clipCustomPath || ''}
                                onChange={val => {
                                    const newValue = { ...value, clipCustomPath: val, clipPath: val };
                                    onChange(newValue);
                                }}
                                placeholder="例: circle(50%) 或 polygon(50% 0%, 0% 100%, 100% 100%)"
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}
                </Space>
            </div>
        </Panel>
    );

    // 边框样式
    const renderBorderStyles = () => (
        <Panel header="边框" itemKey="border">
            <div className={styles.styleSectionContent}>
                <Space vertical style={{ width: '100%' }}>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>边框宽度:</span>
                        <InputNumber
                            value={parseInt(value.borderWidth) || 0}
                            onChange={val => {
                                updateStyle('borderWidth', `${val}px`);
                                // 当设置边框宽度时，如果没有边框样式和颜色，设置默认值
                                if (val > 0) {
                                    if (!value.borderStyle) {
                                        updateStyle('borderStyle', 'solid');
                                    }
                                    if (!value.borderColor) {
                                        updateStyle('borderColor', '#000000');
                                    }
                                }
                            }}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>边框样式:</span>
                        <Select
                            value={value.borderStyle || 'solid'}
                            onChange={val => {
                                updateStyle('borderStyle', val);
                                // 当设置边框样式时，如果没有边框颜色，设置默认颜色
                                if (!value.borderColor) {
                                    updateStyle('borderColor', '#000000');
                                }
                            }}
                            style={{ width: '100%' }}
                        >
                            <Option value="solid">实线</Option>
                            <Option value="dashed">虚线</Option>
                            <Option value="dotted">点线</Option>
                            <Option value="double">双线</Option>
                            <Option value="none">无</Option>
                        </Select>
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>边框颜色:</span>
                        <ColorPicker
                            value={value.borderColor || '#000000'}
                            onChange={val => {
                                updateStyle('borderColor', val);
                                // 当设置边框颜色时，如果没有边框样式，设置默认样式
                                if (!value.borderStyle) {
                                    updateStyle('borderStyle', 'solid');
                                }
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>圆角:</span>
                        <InputNumber
                            value={parseInt(value.borderRadius) || 0}
                            onChange={val => updateStyle('borderRadius', `${val}px`)}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </div>
                </Space>
            </div>
        </Panel>
    );

    // 背景样式
    const renderBackgroundStyles = () => (
        <Panel header="背景" itemKey="background">
            <div className={styles.styleSectionContent}>
                <Space vertical style={{ width: '100%' }}>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>背景类型:</span>
                        <Select
                            value={value.backgroundType || 'solid'}
                            onChange={val => {
                                const newValue = { ...value, backgroundType: val };
                                // 清除之前的背景设置
                                if (val === 'solid') {
                                    newValue.backgroundImage = '';
                                } else if (val === 'gradient') {
                                    newValue.backgroundColor = '';
                                    // 立即应用渐变
                                    updateGradientBackground(newValue);
                                    return; // 避免重复调用onChange
                                }
                                onChange(newValue);
                            }}
                            style={{ width: '100%' }}
                        >
                            <Option value="solid">纯色</Option>
                            <Option value="gradient">渐变</Option>
                        </Select>
                    </div>

                    {(!value.backgroundType || value.backgroundType === 'solid') && (
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>背景颜色:</span>
                            <ColorPicker
                                value={value.backgroundColor || '#ffffff'}
                                onChange={val => updateStyle('backgroundColor', val)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {value.backgroundType === 'gradient' && (
                        <>
                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>渐变类型:</span>
                                <Select
                                    value={value.gradientType || 'linear'}
                                    onChange={val => {
                                        const newValue = { ...value, gradientType: val };
                                        updateGradientBackground(newValue);
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    <Option value="linear">线性渐变</Option>
                                    <Option value="radial">径向渐变</Option>
                                </Select>
                            </div>

                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>起始颜色:</span>
                                <ColorPicker
                                    value={value.gradientStart || '#ffffff'}
                                    onChange={val => {
                                        const newValue = { ...value, gradientStart: val };
                                        updateGradientBackground(newValue);
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div className={styles.styleRow}>
                                <span className={styles.styleLabel}>结束颜色:</span>
                                <ColorPicker
                                    value={value.gradientEnd || '#000000'}
                                    onChange={val => {
                                        const newValue = { ...value, gradientEnd: val };
                                        updateGradientBackground(newValue);
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {(!value.gradientType || value.gradientType === 'linear') && (
                                <div className={styles.styleRow}>
                                    <span className={styles.styleLabel}>渐变角度:</span>
                                    <InputNumber
                                        value={parseInt(value.gradientAngle) || 0}
                                        onChange={val => {
                                            const newValue = { ...value, gradientAngle: val };
                                            updateGradientBackground(newValue);
                                        }}
                                        min={0}
                                        max={360}
                                        style={{ width: '100%' }}
                                        addonAfter="°"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Space>
            </div>
        </Panel>
    );

    // 阴影样式
    const renderShadowStyles = () => (
        <Panel header="阴影" itemKey="shadow">
            <div className={styles.styleSectionContent}>
                <Space vertical style={{ width: '100%' }}>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>水平偏移:</span>
                        <InputNumber
                            value={parseInt(value.boxShadowX) || 0}
                            onChange={val => {
                                const x = val || 0;
                                const y = parseInt(value.boxShadowY) || 0;
                                const blur = parseInt(value.boxShadowBlur) || 0;
                                const spread = parseInt(value.boxShadowSpread) || 0;
                                const color = value.boxShadowColor || '#000000';
                                const shadowValue = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
                                updateStyle('boxShadow', shadowValue);
                                updateStyle('boxShadowX', x);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>垂直偏移:</span>
                        <InputNumber
                            value={parseInt(value.boxShadowY) || 0}
                            onChange={val => {
                                const x = parseInt(value.boxShadowX) || 0;
                                const y = val || 0;
                                const blur = parseInt(value.boxShadowBlur) || 0;
                                const spread = parseInt(value.boxShadowSpread) || 0;
                                const color = value.boxShadowColor || '#000000';
                                const shadowValue = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
                                updateStyle('boxShadow', shadowValue);
                                updateStyle('boxShadowY', y);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>模糊半径:</span>
                        <InputNumber
                            value={parseInt(value.boxShadowBlur) || 0}
                            onChange={val => {
                                const x = parseInt(value.boxShadowX) || 0;
                                const y = parseInt(value.boxShadowY) || 0;
                                const blur = val || 0;
                                const spread = parseInt(value.boxShadowSpread) || 0;
                                const color = value.boxShadowColor || '#000000';
                                const shadowValue = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
                                updateStyle('boxShadow', shadowValue);
                                updateStyle('boxShadowBlur', blur);
                            }}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>扩散半径:</span>
                        <InputNumber
                            value={parseInt(value.boxShadowSpread) || 0}
                            onChange={val => {
                                const x = parseInt(value.boxShadowX) || 0;
                                const y = parseInt(value.boxShadowY) || 0;
                                const blur = parseInt(value.boxShadowBlur) || 0;
                                const spread = val || 0;
                                const color = value.boxShadowColor || '#000000';
                                const shadowValue = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
                                updateStyle('boxShadow', shadowValue);
                                updateStyle('boxShadowSpread', spread);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>阴影颜色:</span>
                        <ColorPicker
                            value={value.boxShadowColor || '#000000'}
                            onChange={val => {
                                const x = parseInt(value.boxShadowX) || 0;
                                const y = parseInt(value.boxShadowY) || 0;
                                const blur = parseInt(value.boxShadowBlur) || 0;
                                const spread = parseInt(value.boxShadowSpread) || 0;
                                const shadowValue = `${x}px ${y}px ${blur}px ${spread}px ${val}`;
                                updateStyle('boxShadow', shadowValue);
                                updateStyle('boxShadowColor', val);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                </Space>
            </div>
        </Panel>
    );

    // 文字样式
    const renderTextStyles = () => {
        if (elementType !== 'text') return null;

        return (
            <Panel header="文字" itemKey="text">
                <div className={styles.styleSectionContent}>
                    <Space vertical style={{ width: '100%' }}>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>字体大小:</span>
                            <InputNumber
                                value={parseInt(value.fontSize) || 16}
                                onChange={val => updateStyle('fontSize', `${val}px`)}
                                min={8}
                                max={100}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>字体颜色:</span>
                            <ColorPicker
                                value={value.color || '#000000'}
                                onChange={val => updateStyle('color', val)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>字体粗细:</span>
                            <Select
                                value={value.fontWeight || 'normal'}
                                onChange={val => updateStyle('fontWeight', val)}
                                style={{ width: '100%' }}
                            >
                                <Option value="normal">正常</Option>
                                <Option value="bold">粗体</Option>
                                <Option value="100">极细</Option>
                                <Option value="200">细体</Option>
                                <Option value="300">轻体</Option>
                                <Option value="400">正常</Option>
                                <Option value="500">中等</Option>
                                <Option value="600">半粗体</Option>
                                <Option value="700">粗体</Option>
                                <Option value="800">特粗</Option>
                                <Option value="900">黑体</Option>
                            </Select>
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>文字对齐:</span>
                            <Select
                                value={value.textAlign || 'left'}
                                onChange={val => updateStyle('textAlign', val)}
                                style={{ width: '100%' }}
                            >
                                <Option value="left">左对齐</Option>
                                <Option value="center">居中</Option>
                                <Option value="right">右对齐</Option>
                                <Option value="justify">两端对齐</Option>
                            </Select>
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>行高:</span>
                            <InputNumber
                                value={parseFloat(value.lineHeight) || 1.4}
                                onChange={val => updateStyle('lineHeight', val)}
                                min={0.5}
                                max={3}
                                step={0.1}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>字间距:</span>
                            <InputNumber
                                value={parseInt(value.letterSpacing) || 0}
                                onChange={val => updateStyle('letterSpacing', `${val}px`)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>文字装饰:</span>
                            <Select
                                value={value.textDecoration || 'none'}
                                onChange={val => updateStyle('textDecoration', val)}
                                style={{ width: '100%' }}
                            >
                                <Option value="none">无</Option>
                                <Option value="underline">下划线</Option>
                                <Option value="overline">上划线</Option>
                                <Option value="line-through">删除线</Option>
                            </Select>
                        </div>
                        <div className={styles.styleRow}>
                            <span className={styles.styleLabel}>字体族:</span>
                            <Input
                                value={value.fontFamily || ''}
                                onChange={val => updateStyle('fontFamily', val)}
                                placeholder="如: Arial, sans-serif"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </Space>
                </div>
            </Panel>
        );
    };

    // 变换样式
    const renderTransformStyles = () => (
        <Panel header="变换" itemKey="transform">
            <div className={styles.styleSectionContent}>
                <Space vertical style={{ width: '100%' }}>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>Z轴旋转:</span>
                        <InputNumber
                            value={parseInt(value.rotate) || 0}
                            onChange={val => {
                                const rotate = val || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('rotate', rotate);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>X轴旋转:</span>
                        <InputNumber
                            value={parseInt(value.rotateX) || 0}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = val || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('rotateX', rotateX);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>Y轴旋转:</span>
                        <InputNumber
                            value={parseInt(value.rotateY) || 0}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = val || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('rotateY', rotateY);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>水平缩放:</span>
                        <InputNumber
                            value={parseFloat(value.scaleX) || 1}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = val || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('scaleX', scaleX);
                            }}
                            min={0.1}
                            max={3}
                            step={0.1}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>垂直缩放:</span>
                        <InputNumber
                            value={parseFloat(value.scaleY) || 1}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = val || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('scaleY', scaleY);
                            }}
                            min={0.1}
                            max={3}
                            step={0.1}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>水平倾斜:</span>
                        <InputNumber
                            value={parseInt(value.skewX) || 0}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = val || 0;
                                const skewY = parseInt(value.skewY) || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('skewX', skewX);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.styleRow}>
                        <span className={styles.styleLabel}>垂直倾斜:</span>
                        <InputNumber
                            value={parseInt(value.skewY) || 0}
                            onChange={val => {
                                const rotate = parseInt(value.rotate) || 0;
                                const rotateX = parseInt(value.rotateX) || 0;
                                const rotateY = parseInt(value.rotateY) || 0;
                                const scaleX = parseFloat(value.scaleX) || 1;
                                const scaleY = parseFloat(value.scaleY) || 1;
                                const skewX = parseInt(value.skewX) || 0;
                                const skewY = val || 0;
                                const transformValue = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scaleX}, ${scaleY}) skew(${skewX}deg, ${skewY}deg)`;
                                updateStyle('transform', transformValue);
                                updateStyle('skewY', skewY);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                </Space>
            </div>
        </Panel>
    );

    return (
        <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>样式设置:</label>
            <Collapse activeKey={activeKey} onChange={setActiveKey}>
                {renderTextStyles()}
                {elementType !== 'module' && renderBackgroundStyles()}
                {renderBorderStyles()}
                {renderShadowStyles()}
                {renderLayoutStyles()}
                {renderTransformStyles()}
            </Collapse>
        </div>
    );
};

export default StyleEditor;
